import { useState } from 'react'
import Modal from './Modal'
import Boton from './Boton'
import { notasVentaApi } from '../api/notasVenta'
import { copiarTexto } from '../utils/copiar'
import { fmtFecha, fmtFechaHora } from '../utils/fecha'
import { colors, font, text, space, radius, sx } from '../design/tokens'

/**
 * "⭐ Encuesta de satisfacción" (Sugerencia #111, 23 oct 2026): el vendedor le
 * manda al cliente el link de la encuesta de COMPRA — la misma del mail de las
 * 48 h, con el mismo token. Se usa en el detalle de la NV y en el del remito.
 *
 * La encuesta se pide (y se crea si no existe) recién al abrir el modal, nunca
 * al cargar la página. Copiar o mandar por WhatsApp sella el envío manual en
 * el backend; con eso el mail automático ya no sale.
 *
 * Props: nvId (la NV ya facturada — el padre decide si mostrar el botón).
 */
export default function BotonEncuestaSatisfaccion({ nvId, sm = false }) {
  const [abierto, setAbierto] = useState(false)
  const [cargando, setCargando] = useState(false)
  const [error, setError] = useState('')
  const [enc, setEnc] = useState(null)
  const [mensaje, setMensaje] = useState('')
  const [telefono, setTelefono] = useState('')
  const [copiado, setCopiado] = useState(null)

  const abrir = async () => {
    setAbierto(true); setCargando(true); setError(''); setCopiado(null)
    try {
      const { data } = await notasVentaApi.obtenerEncuesta(nvId)
      setEnc(data)
      setTelefono(data.telefono || '')
      setMensaje(mensajeDefault(data.nombre, data.link))
    } catch (err) {
      setError(primerError(err) || 'No se pudo obtener la encuesta.')
    } finally { setCargando(false) }
  }

  const sellar = async (canal) => {
    try {
      const { data } = await notasVentaApi.marcarEncuestaEnviada(nvId, canal)
      setEnc(data)
    } catch (err) {
      setError(primerError(err) || 'No se pudo registrar el envío.')
    }
  }

  const copiar = async () => {
    const ok = await copiarTexto(enc.link)
    setCopiado(ok ? 'listo' : 'error')
    setTimeout(() => setCopiado(null), 2200)
    if (ok) sellar('copiar')
  }

  const tel = telefono.replace(/\D/g, '')
  const enviarWhatsApp = () => {
    if (!tel) return
    const destino = tel.startsWith('54') ? tel : '54' + tel
    // La pestaña se abre ANTES del await: si no, el navegador la bloquea como popup.
    window.open(`https://wa.me/${destino}?text=${encodeURIComponent(mensaje)}`, '_blank', 'noopener')
    sellar('whatsapp')
  }

  const respondida = enc?.estado === 'respondida'

  return (
    <>
      <Boton variante="secondary" sm={sm} onClick={abrir}
        title="Mandarle al cliente el link de la encuesta de satisfacción de su compra">
        ⭐ Encuesta de satisfacción
      </Boton>
      {abierto && (
        <Modal title="⭐ Encuesta de satisfacción" onClose={() => setAbierto(false)} maxWidth="560px">
          {cargando && <div style={st.meta}>Cargando…</div>}
          {error && <div style={st.error}>{error}</div>}
          {enc && !cargando && (
            <div style={st.col}>
              <div style={respondida ? st.estadoOk : st.estado}>{textoEstado(enc)}</div>

              {!respondida && (<>
                <div>
                  <div style={st.label}>Link de la encuesta</div>
                  <div style={st.fila}>
                    <input readOnly value={enc.link} style={{ ...st.input, ...sx.mono, flex: 1 }} onFocus={e => e.target.select()} />
                    <Boton variante="secondary" onClick={copiar}>
                      {copiado === 'listo' ? '✓ Copiado' : copiado === 'error' ? 'No se pudo copiar' : '📋 Copiar link'}
                    </Boton>
                  </div>
                </div>

                <div>
                  <div style={st.label}>Mensaje de WhatsApp</div>
                  <textarea value={mensaje} onChange={e => setMensaje(e.target.value)} rows={5} style={{ ...st.input, resize: 'vertical', width: '100%', boxSizing: 'border-box' }} />
                </div>

                <div>
                  <div style={st.label}>Teléfono del cliente</div>
                  <div style={st.fila}>
                    <input value={telefono} onChange={e => setTelefono(e.target.value)} placeholder="Sin teléfono en la ficha — cargalo acá" style={{ ...st.input, ...sx.mono, flex: 1 }} />
                    <Boton variante="primary" disabled={!tel || !mensaje.trim()} onClick={enviarWhatsApp}>
                      Enviar por WhatsApp
                    </Boton>
                  </div>
                </div>

                <div style={st.meta}>
                  Si la mandás vos (copiar o WhatsApp), el mail automático de las 48 h ya no sale.
                </div>
              </>)}

              <div style={st.acciones}>
                <Boton variante="ghost" onClick={() => setAbierto(false)}>Cerrar</Boton>
              </div>
            </div>
          )}
        </Modal>
      )}
    </>
  )
}

function mensajeDefault(nombre, link) {
  const saludo = nombre ? `Hola ${nombre}` : 'Hola'
  return `${saludo}, ¡gracias por tu compra en Ligier! Nos ayudás mucho si nos contás cómo fue tu experiencia, te lleva menos de un minuto: ${link}`
}

function textoEstado(enc) {
  if (enc.estado === 'respondida') return `✓ Ya respondió el ${fmtFecha(enc.respondida_at)}`
  const partes = []
  if (enc.enviada_manual_at) partes.push(`Enviada por ${enc.enviada_manual_por || 'un usuario'} el ${fmtFechaHora(enc.enviada_manual_at)}`)
  if (enc.mail_enviado_at) partes.push(`El mail automático salió el ${fmtFecha(enc.mail_enviado_at)}`)
  if (partes.length === 0) return 'Todavía no se le mandó la encuesta.'
  return `${partes.join(' · ')} — todavía no respondió.`
}

function primerError(err) {
  const d = err?.response?.data
  if (d?.errors) return Object.values(d.errors).flat()[0]
  return d?.message
}

const st = {
  col: { display: 'flex', flexDirection: 'column', gap: space.xl },
  fila: { display: 'flex', gap: space.md, alignItems: 'center' },
  label: { fontSize: text.sec, fontWeight: 600, color: colors.textSec, marginBottom: space.xs },
  input: { fontFamily: font.sans, fontSize: text.body, color: colors.text, padding: '6px 10px', border: `1px solid ${colors.border}`, borderRadius: radius.sm, background: colors.bg },
  estado: { fontSize: text.body, color: colors.textSub, background: colors.bgSoft, border: `1px solid ${colors.border}`, borderRadius: radius.sm, padding: `${space.md}px ${space.lg}px` },
  estadoOk: { fontSize: text.body, fontWeight: 600, color: colors.ok, background: colors.okBg, borderRadius: radius.sm, padding: `${space.md}px ${space.lg}px` },
  meta: { fontSize: text.sec, color: colors.textMeta },
  error: { fontSize: text.body, color: colors.err, background: colors.errBg, borderRadius: radius.sm, padding: `${space.md}px ${space.lg}px`, marginBottom: space.lg },
  acciones: { display: 'flex', justifyContent: 'flex-end' },
}
